import React, { useEffect, useState, useCallback } from 'react'; // Zdna useCallback
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Trash2, Edit, Save, X } from "lucide-react";

const UsersPage = () => {
    const [allUsers, setAllUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [filterType, setFilterType] = useState('ALL'); 
    const [loading, setLoading] = useState(true);
    
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'ROLE_ADMIN';
    const isManager = currentUser?.role === 'ROLE_MANAGER';
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({ username: '', entity: '' });
    const fetchUsers = useCallback(async () => {
        try {
            const response = await api.get('/users'); 
            const usersList = response.data.filter(u => u.username !== currentUser.sub);
            setAllUsers(usersList);
            setFilteredUsers(usersList);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    }, [currentUser.sub]); 

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);
    const handleEditClick = (u) => {
        setEditId(u.id);
        setEditForm({ username: u.username, entity: u.entity });
    };

    const handleSaveUpdate = async (id) => {
        try {
            await api.put(`/users/update/${id}`, editForm);
            setEditId(null);
            fetchUsers();
            alert("User updated successfully!");
        } catch (error) {
            alert("Error updating user.");
        }
    };
    const handleRoleChange = async (id, newRole) => {
        let endpoint = '';
        let actionName = '';

        if (newRole === 'MANAGER') {
            endpoint = `/users/promote-to-manager/${id}`;
            actionName = 'Promote to Manager';
        } else if (newRole === 'TL') {
            endpoint = `/users/promote-to-tl/${id}`;
            actionName = 'Promote to Team Leader';
        } else if (newRole === 'MAILER') {
            endpoint = `/users/demote-to-mailer/${id}`;
            actionName = 'Demote to Mailer';
        }

        if (!window.confirm(`Are you sure you want to: ${actionName}?`)) return;

        try {
            await api.put(endpoint);
            fetchUsers();
            alert("Operation successful!");
        } catch (error) {
            console.error(error);
            alert("Error: You do not have permission.");
        }
    };

    // Filter Logic
    useEffect(() => {
        if (filterType === 'ALL') {
            setFilteredUsers(allUsers);
        } else if (filterType === 'ACTIVE') {
            setFilteredUsers(allUsers.filter(u => u.active === true));
        } else if (filterType === 'PENDING') {
            setFilteredUsers(allUsers.filter(u => u.active === false));
        }
    }, [filterType, allUsers]);


    const handleApprove = async (id) => {
        try {
            await api.put(`/users/approve/${id}`);
            fetchUsers();
        } catch (error) { alert("Error approving user."); }
    };

    const handleReject = async (id) => {
        if (!window.confirm("Disable this user?")) return;
        try {
            await api.put(`/users/reject/${id}`);
            fetchUsers();
        } catch (error) { alert("Error disabling user."); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to permanently delete this user?")) return;
        try {
            await api.delete(`/users/${id}`);
            fetchUsers();
        } catch (error) { alert("Error deleting user."); }
    };

    if (loading) return <div className="text-center mt-5">Loading...</div>;

return (
        <div className="container-fluid p-4">
            <h2 className="mb-4 text-primary fw-bold">User Management</h2>

            {/* Filter Tabs */}
            <div className="btn-group mb-4 shadow-sm">
                <button className={`btn ${filterType === 'ALL' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilterType('ALL')}>All ({allUsers.length})</button>
                <button className={`btn ${filterType === 'ACTIVE' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setFilterType('ACTIVE')}>Active ({allUsers.filter(u => u.active).length})</button>
                <button className={`btn ${filterType === 'PENDING' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => setFilterType('PENDING')}>Pending ({allUsers.filter(u => !u.active).length})</button>
            </div>

            <div className="card shadow border-0">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light text-secondary">
                            <tr>
                                <th className="ps-4">Username</th>
                                <th>Entity</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((u) => (
                                <tr key={u.id}>
                                    <td className="ps-4">
                                        {editId === u.id ? (
                                            <input type="text" className="form-control form-control-sm" value={editForm.username} onChange={(e) => setEditForm({...editForm, username: e.target.value})} />
                                        ) : (
                                            <span className="fw-bold text-dark">{u.username}</span>
                                        )}
                                    </td>
                                    <td>
                                        {editId === u.id ? (
                                            <input type="text" className="form-control form-control-sm" value={editForm.entity} onChange={(e) => setEditForm({...editForm, entity: e.target.value})} />
                                        ) : (
                                            <span className="badge bg-light text-dark border">{u.entity}</span> 
                                        )}
                                    </td>
                                    <td>
                                        <span className={`badge ${u.role === 'ROLE_ADMIN' ? 'bg-danger' : u.role === 'ROLE_MANAGER' ? 'bg-info text-dark' : 'bg-secondary'}`}>
                                            {u.role.replace('ROLE_', '')}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`badge ${u.active ? 'bg-success' : 'bg-warning'} bg-opacity-10 text-${u.active ? 'success' : 'warning'} px-3 py-2 rounded-pill`}>
                                            {u.active ? 'Active' : 'Pending'}
                                        </span>
                                    </td>
                                    <td className="text-end pe-4">
                                        <div className="d-flex justify-content-end gap-2">
                                            {editId === u.id ? (
                                                <>
                                                    <button onClick={() => handleSaveUpdate(u.id)} className="btn btn-sm btn-success"><Save size={16} /></button>
                                                    <button onClick={() => setEditId(null)} className="btn btn-sm btn-secondary"><X size={16} /></button>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Edit Button */}
                                                    <button onClick={() => handleEditClick(u)} className="btn btn-sm btn-outline-secondary"><Edit size={16} /></button>
                                                    
                                                    {/* Change Role Dropdown (Uses handleRoleChange) */}
                                                    {(isAdmin || isManager) && u.active && (
                                                        <div className="dropdown">
                                                            <button className="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">Role</button>
                                                            <ul className="dropdown-menu">
                                                                {isAdmin && <li><button className="dropdown-item" onClick={() => handleRoleChange(u.id, 'MANAGER')}>Manager</button></li>}
                                                                <li><button className="dropdown-item" onClick={() => handleRoleChange(u.id, 'TL')}>Team Leader</button></li>
                                                                <li><button className="dropdown-item" onClick={() => handleRoleChange(u.id, 'MAILER')}>Mailer</button></li>
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {/*  Status Actions (Uses handleApprove / handleReject) */}
                                                    {!u.active ? (
                                                        <button onClick={() => handleApprove(u.id)} className="btn btn-sm btn-success">Approve</button>
                                                    ) : (
                                                        <button onClick={() => handleReject(u.id)} className="btn btn-sm btn-warning">Disable</button>
                                                    )}

                                                    {/*  Delete Action */}
                                                    {isAdmin && (
                                                        <button onClick={() => handleDelete(u.id)} className="btn btn-sm btn-outline-danger"><Trash2 size={16} /></button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UsersPage;